import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, FileText, Plus } from 'lucide-react';
import { CompetitionStatus, type Competition } from '@/api/types';
import { useOrganizerOverview, type CompetitionOverview } from '@/hooks/useOrganizerOverview';
import { useJudgesProgress } from '@/hooks/useJudging';
import { useUsersByIds } from '@/hooks/useUsers';
import { useAuthStore } from '@/store/authStore';
import { PageSpinner } from '@/components/ui/Spinner';
import { Alert, EmptyState, ErrorState } from '@/components/ui/Alert';
import { getApiErrorMessage } from '@/api/client';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { competitionStatusLabel, competitionStatusTone } from '@/lib/competitions';
import { describeDeadline, formatDate, formatLongDate } from '@/lib/dates';

const NUMBER = new Intl.NumberFormat('sl-SI');
const n = (value: number) => NUMBER.format(value);

function percent(part: number, whole: number): number {
  return whole > 0 ? Math.min(100, Math.round((part / whole) * 100)) : 0;
}

/** Which date matters right now for a competition in this status. */
function relevantDeadline(c: Competition): { label: string; iso?: string | null } {
  switch (c.status) {
    case CompetitionStatus.Draft:
    case CompetitionStatus.Scheduled:
      return { label: 'Odprtje', iso: c.submissionOpensAt ?? c.startDate };
    case CompetitionStatus.Active:
      return { label: 'Rok za oddajo', iso: c.submissionDeadline };
    case CompetitionStatus.Judging:
      return { label: 'Rok za ocenjevanje', iso: c.judgingDeadline };
    default:
      return { label: 'Zaključeno', iso: c.endDate };
  }
}

function Kpi({
  label,
  value,
  hint,
  tone = 'default',
  children,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'warning';
  children?: ReactNode;
}) {
  return (
    <Card className="flex flex-col gap-1.5 px-5 py-4">
      <span className="text-sm text-fg-muted">{label}</span>
      <span
        className={`font-mono text-3xl font-medium leading-none tabular-nums tracking-tight ${
          tone === 'warning' ? 'text-warning' : 'text-fg'
        }`}
      >
        {value}
      </span>
      {hint && <span className="text-xs text-fg-muted">{hint}</span>}
      {children}
    </Card>
  );
}

function JudgesPanel({ row }: { row: CompetitionOverview }) {
  const { usersById } = useUsersByIds(row.judgeUserIds);
  const judgesProgress = useJudgesProgress(row.competition.id);
  const progressByUser = new Map((judgesProgress.data?.items ?? []).map((p) => [p.userId, p]));
  const describe = (id: string) => {
    const u = usersById.get(id);
    return u ? u.displayName || `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email : '…';
  };
  const initials = (id: string) =>
    describe(id)
      .split(/\s+/)
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();

  return (
    <Card>
      <CardHeader className="flex items-center justify-between gap-2">
        <h2 className="font-semibold text-fg">Sodniki · {row.competition.name}</h2>
        <span className="font-mono text-[11px] uppercase tracking-[0.06em] text-fg-muted">ocenjevanje</span>
      </CardHeader>
      <CardBody className="flex flex-col gap-3">
        <div>
          <div className="mb-1.5 flex items-baseline justify-between text-sm">
            <span className="text-fg-secondary">Oddanih ocen</span>
            <span className="font-mono tabular-nums text-fg">
              {n(row.scoresReceived)} / {n(row.scoresExpected)}
            </span>
          </div>
          <ProgressBar value={row.scoresReceived} max={row.scoresExpected} />
          <p className="mt-1.5 text-xs text-fg-muted">
            {row.judgeUserIds.length === 0
              ? 'Tekmovanju še ni dodeljen noben sodnik.'
              : `${n(row.judgeUserIds.length)} ${row.judgeUserIds.length === 1 ? 'sodnik' : row.judgeUserIds.length === 2 ? 'sodnika' : 'sodniki'} × ${n(row.accepted)} sprejetih prispevkov.`}
          </p>
        </div>
        {row.judgeUserIds.length > 0 && (
          <ul className="flex flex-col divide-y divide-line">
            {row.judgeUserIds.map((id) => {
              const p = progressByUser.get(id);
              return (
                <li key={id} className="flex items-center gap-3 py-2">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-2 font-mono text-[11px] font-medium text-fg">
                    {initials(id)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-sm font-medium text-fg">{describe(id)}</span>
                      {p ? (
                        <span className="shrink-0 font-mono text-xs tabular-nums text-fg-secondary">
                          {p.scored} / {p.total}
                          {p.total > 0 && p.scored === p.total && <span className="ml-1 text-success">✓</span>}
                        </span>
                      ) : (
                        <span className="font-mono text-[10px] uppercase tracking-[0.06em] text-fg-muted">sodnik</span>
                      )}
                    </div>
                    {p && p.total > 0 && <ProgressBar value={p.scored} max={p.total} className="mt-1" />}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <Link to={`/organizer/competitions/${row.competition.id}/judges`} className="self-start">
          <Button variant="secondary" size="sm">
            Upravljaj sodnike
          </Button>
        </Link>
      </CardBody>
    </Card>
  );
}

export function OrganizerOverviewPage() {
  const user = useAuthStore((s) => s.user);
  const activeCompetitionId = useAuthStore((s) => s.activeCompetitionId);
  const { rows, totals, isLoading, isError, error, refetch } = useOrganizerOverview();

  if (isLoading) return <PageSpinner />;
  if (isError) return <ErrorState message={getApiErrorMessage(error)} onRetry={() => refetch()} />;

  const awaiting = rows
    .flatMap((r) => r.awaitingModeration.map((s) => ({ submission: s, competition: r.competition })))
    .sort((a, b) => (a.submission.submittedAt ?? '').localeCompare(b.submission.submittedAt ?? ''))
    .slice(0, 6);
  const withRequests = rows.filter((r) => r.pendingRequests > 0);
  const attention = totals.awaitingModeration + totals.pendingRequests;
  const judgingRows = rows.filter((r) => r.competition.status === CompetitionStatus.Judging);
  const focusJudging = judgingRows.find((r) => r.competition.id === activeCompetitionId) ?? judgingRows[0];
  const anyRowError = rows.some((r) => r.isError);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="font-mono text-xs uppercase tracking-[0.06em] text-fg-muted">{formatLongDate()}</span>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-fg">
            Pregled{user?.displayName ? ` · ${user.displayName}` : ''}
          </h1>
        </div>
        <div className="flex gap-2">
          <Link to="/organizer/reports">
            <Button variant="secondary">
              <FileText className="h-4 w-4" aria-hidden="true" />
              Poročila
            </Button>
          </Link>
          <Link to="/organizer/competitions">
            <Button>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Novo tekmovanje
            </Button>
          </Link>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="Še ne upravljate nobenega tekmovanja"
          description="Ustvarite tekmovanje v razdelku Upravljanje tekmovanj; tukaj se bodo zbirali prispevki, sodniki in napredek ocenjevanja."
        />
      ) : (
        <>
          {anyRowError && <Alert variant="warning">Nekaterih podatkov ni bilo mogoče naložiti; številke so lahko nepopolne.</Alert>}

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi
              label="Aktivna tekmovanja"
              value={n(totals.active + totals.judging)}
              hint={`${n(totals.active)} sprejema prispevke · ${n(totals.judging)} v ocenjevanju${totals.scheduled ? ` · ${n(totals.scheduled)} načrtovano` : ''}`}
            />
            <Kpi
              label="Prispevki"
              value={n(totals.entries)}
              hint={`${n(totals.accepted)} sprejetih · ${n(totals.awaitingModeration)} čaka na pregled`}
            />
            <Kpi
              label="Napredek ocenjevanja"
              value={totals.scoresExpected > 0 ? `${percent(totals.scoresReceived, totals.scoresExpected)} %` : '—'}
              hint={
                totals.scoresExpected > 0
                  ? `${n(totals.scoresReceived)} od ${n(totals.scoresExpected)} ocen oddanih`
                  : 'Nobeno tekmovanje ni v ocenjevanju'
              }
            >
              {totals.scoresExpected > 0 && (
                <ProgressBar value={totals.scoresReceived} max={totals.scoresExpected} className="mt-1" />
              )}
            </Kpi>
            <Kpi
              label="Potrebuje pozornost"
              value={n(attention)}
              tone={attention > 0 ? 'warning' : 'default'}
              hint={`${n(totals.awaitingModeration)} prispevkov čaka na odločitev · ${n(totals.pendingRequests)} prošenj za sodelovanje`}
            />
          </div>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1.7fr)_minmax(300px,1fr)]">
            <Card className="min-w-0">
              <CardHeader className="flex items-center justify-between gap-2">
                <h2 className="font-semibold text-fg">Tekmovanja</h2>
                <Link to="/organizer/competitions" className="text-sm font-medium text-fg-secondary hover:text-fg">
                  Upravljanje
                </Link>
              </CardHeader>
              <div className="overflow-x-auto">
                <table className="min-w-[640px] w-full text-sm">
                  <thead>
                    <tr className="h-9 border-b border-line font-mono text-[11px] uppercase tracking-[0.06em] text-fg-muted">
                      <th scope="col" className="px-5 text-left font-medium">Tekmovanje</th>
                      <th scope="col" className="px-3 text-left font-medium">Status</th>
                      <th scope="col" className="px-3 text-right font-medium">Prispevki</th>
                      <th scope="col" className="px-3 text-left font-medium">Ocenjevanje</th>
                      <th scope="col" className="px-5 text-left font-medium">Rok</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const c = row.competition;
                      const deadline = relevantDeadline(c);
                      const categories = c.categories?.length ?? 0;
                      const judgingActive = c.status >= CompetitionStatus.Judging && c.status < CompetitionStatus.Archived;
                      return (
                        <tr key={c.id} className="h-14 border-b border-line transition-colors duration-fast last:border-b-0 hover:bg-surface-2">
                          <td className="min-w-0 px-5 py-2">
                            <Link to={`/competitions/${c.id}`} className="block min-w-0">
                              <span className="block truncate font-medium text-fg">{c.name}</span>
                              <span className="block text-xs text-fg-muted">
                                {categories === 0
                                  ? 'Brez kategorij'
                                  : categories === 1
                                    ? '1 kategorija'
                                    : categories === 2
                                      ? '2 kategoriji'
                                      : `${categories} kategorij${categories <= 4 ? 'e' : ''}`}
                                {row.awaitingModeration.length > 0 && ` · ${n(row.awaitingModeration.length)} čaka na pregled`}
                              </span>
                            </Link>
                          </td>
                          <td className="px-3 py-2">
                            <Badge tone={competitionStatusTone(c.status)}>{competitionStatusLabel(c.status)}</Badge>
                          </td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-fg">
                            {row.isLoading ? <span className="text-fg-muted">…</span> : n(row.entries)}
                          </td>
                          <td className="px-3 py-2">
                            {judgingActive ? (
                              <div className="flex items-center gap-2">
                                <ProgressBar value={row.scoresReceived} max={row.scoresExpected} className="w-24" />
                                <span className="w-10 text-right font-mono text-xs tabular-nums text-fg-muted">
                                  {row.scoresExpected > 0 ? `${percent(row.scoresReceived, row.scoresExpected)} %` : '—'}
                                </span>
                              </div>
                            ) : (
                              <span className="text-fg-muted">—</span>
                            )}
                          </td>
                          <td className="px-5 py-2">
                            <span className="block font-mono text-xs tabular-nums text-fg">{describeDeadline(deadline.iso)}</span>
                            <span className="block text-xs text-fg-muted">
                              {deadline.label}
                              {deadline.iso && describeDeadline(deadline.iso) !== formatDate(deadline.iso) ? ` · ${formatDate(deadline.iso)}` : ''}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>

            <div className="flex min-w-0 flex-col gap-4">
              <Card>
                <CardHeader className="flex items-center justify-between gap-2">
                  <h2 className="font-semibold text-fg">Čaka na vašo odločitev</h2>
                  {attention > 0 && (
                    <span className="inline-flex h-6 items-center rounded-full bg-warning-subtle px-2 font-mono text-[11px] tabular-nums text-warning">
                      {n(attention)}
                    </span>
                  )}
                </CardHeader>
                <CardBody className="flex flex-col gap-3">
                  {attention === 0 && <p className="text-sm text-fg-muted">Vse je pregledano. Nobenih odprtih prispevkov ali prošenj.</p>}
                  {awaiting.length > 0 && (
                    <ul className="flex flex-col divide-y divide-line">
                      {awaiting.map(({ submission, competition }) => (
                        <li key={submission.id}>
                          <Link
                            to={`/submissions/${submission.id}`}
                            className="group flex items-center gap-3 py-2 text-sm"
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-medium text-fg">{submission.title}</span>
                              <span className="block truncate text-xs text-fg-muted">
                                {competition.name} · oddano {formatDate(submission.submittedAt ?? submission.createdAt)}
                              </span>
                            </span>
                            <ChevronRight className="h-4 w-4 shrink-0 text-fg-muted transition-colors group-hover:text-fg" aria-hidden="true" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                  {totals.awaitingModeration > awaiting.length && (
                    <p className="text-xs text-fg-muted">in še {n(totals.awaitingModeration - awaiting.length)} prispevkov.</p>
                  )}
                  {withRequests.length > 0 && (
                    <ul className="flex flex-col divide-y divide-line border-t border-line pt-1">
                      {withRequests.map((r) => (
                        <li key={r.competition.id}>
                          <Link
                            to={`/organizer/competitions/${r.competition.id}/participants`}
                            className="group flex items-center gap-3 py-2 text-sm"
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-medium text-fg">
                                {n(r.pendingRequests)} {r.pendingRequests === 1 ? 'prošnja' : r.pendingRequests === 2 ? 'prošnji' : 'prošenj'} za sodelovanje
                              </span>
                              <span className="block truncate text-xs text-fg-muted">{r.competition.name}</span>
                            </span>
                            <ChevronRight className="h-4 w-4 shrink-0 text-fg-muted transition-colors group-hover:text-fg" aria-hidden="true" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardBody>
              </Card>

              {focusJudging && <JudgesPanel row={focusJudging} />}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
